import { CircularProgress } from '@material-ui/core';
import cn from 'classnames';
import { hideLoginModal, showRemindModal, showVerificationModal } from 'features/login/store/login.action';
import { ErrorCodes, LoginErrors, LoginResponse } from 'features/login/util/utility';
import { useBiometry } from 'features/webView/components/hooks/useBiometry';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Button from 'shared/components/form/button';
import Checkbox from 'shared/components/form/checkbox';
import CloseForm from 'shared/components/form/closeForm';
import FormRow from 'shared/components/form/formRow';
import HidePassword from 'shared/components/form/hidePassword';
import { HelmetMetaTags } from 'shared/components/HelmetMetaTags';
import { APP_URL, SectionNavigation } from 'shared/constants/common';
import { LoginEvents, RegisterEvents, setDataLayer } from 'shared/constants/datalayer';
import { loginFormValidationSchema } from 'shared/constants/validation-schema';
import { useNewRegistrationPanelFlag } from 'shared/featureFlags';
import { useRecaptcha } from 'shared/hooks/useRecaptcha';
import useSectionNavigation from 'shared/hooks/useSectionNavigation';
import { getUserDetails, isFieldValueValid, isMobile, showModalForUnverifiedAccount } from 'shared/util/utility';
import InputText from '../../../../shared/components/form/inputText';
import { CouponState, State } from '../../../../shared/interface';
import './styles.scss';

interface LoginUser {
	login: string;
	password: string;
	keepSignedIn: boolean;
	recaptchaToken: string;
}

interface LoginFormInterface {
	email: string;
	password: string;
	keepSignedIn: boolean;
	enableBiometry?: boolean;
}

interface LocationState {
	errorMessage: string;
}

interface FormProps {
	actionType: string;
	initialValues: any;
	goBack?: () => void;
	loginUser: (data: LoginUser, coupon: CouponState) => Promise<LoginResponse>;
	handleNavTab: () => void;
	onIconClick?: () => void;
	disableVerificationModal?: boolean;
}

const LoginForm = (props: FormProps & Partial<Pick<HTMLDivElement, 'className'>>) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { goToFirstNavigationView } = useSectionNavigation();
	const location = useLocation<LocationState>();
	const [loginMessage, setLoginMessage] = useState('');
	const [inputType, setInputType] = useState('password');
	const [loading, setLoading] = useState(false);
	const [loginChanged, setLoginChanged] = useState(false);
	const [passwordChanged, setPasswordChanged] = useState(false);
	const [currentError, setCurrentError] = useState(0);
	const {
		subscribeToBiometricScan,
		triggerSetIsBiometryEnabled,
		triggerNewBiometricSession,
		triggerSetLoginFailed,
		isBiometryAvailable,
		isBiometryEnabled,
		triggerBiometricScan,
		refresh,
	} = useBiometry();
	const [isInitialScanCompleted, setIsInitialScanCompleted] = useState<boolean>(false);
	const history = useHistory();
	const showBiometricError = location.state?.errorMessage || '';
	const coupon = useSelector<State, CouponState>((state) => state.coupon);
	const newRegistrationPanelFlag = useNewRegistrationPanelFlag();
	const localNewRegistrationPanelFlag = newRegistrationPanelFlag ? newRegistrationPanelFlag.value : false;
	const { execute: executeRecaptcha } = useRecaptcha();

	useEffect(() => {
		refresh();
	}, [refresh]);

	useEffect(() => {
		if (isBiometryAvailable && isBiometryEnabled && !isInitialScanCompleted) {
			triggerBiometricScan();
			setIsInitialScanCompleted(true);
		}
	}, [setIsInitialScanCompleted, isInitialScanCompleted, isBiometryAvailable, isBiometryEnabled, triggerBiometricScan]);

	const handleOpenReminder = () => {
		dispatch(hideLoginModal());
		dispatch(showRemindModal());
	};

	const handleRedirection = () => {
		const isLoginRoute = window.location.pathname.includes(APP_URL.login);
		const isPrevRouteActivateAccount = history.location.state === APP_URL.activateAccount;
		const isPrevRoute = !!history.location.state;
		const isPrevRouteKuponKibica = history.location.state === APP_URL.kuponKibica;
		const isRegistrationRoute = window.location.pathname.includes(APP_URL.register);

		if (isRegistrationRoute) {
			history.push(APP_URL.home);
			return;
		}

		if (isLoginRoute && (!isPrevRoute || isPrevRouteActivateAccount)) {
			history.push(APP_URL.home);
			return;
		}

		if (isLoginRoute && isPrevRoute && !isPrevRouteKuponKibica) {
			history.push(history.location.state as string);
		} else {
			props.goBack?.();
		}

		dispatch(hideLoginModal());

		const userDetails = getUserDetails();
		if (!props.disableVerificationModal && showModalForUnverifiedAccount(userDetails)) {
			dispatch(showVerificationModal());
		}
	};

	const isLoginOrEmailChanged = () => {
		if (currentError === ErrorCodes.wrongPassword) {
			return loginChanged || passwordChanged;
		} else {
			return true;
		}
	};

	const handleRegister = () => {
		if (window.location.pathname === APP_URL.register) {
			dispatch(hideLoginModal());
			return;
		}

		if (isMobile()) {
			history.push({ pathname: APP_URL.register });
		} else {
			dispatch(hideLoginModal());
			localNewRegistrationPanelFlag
				? history.push({ pathname: APP_URL.register })
				: goToFirstNavigationView(SectionNavigation.REGISTRATION_METHOD);
		}
	};

	const isFirstLogin = sessionStorage.getItem('isNewAccount');

	const onLoginSubmit = async (data: LoginFormInterface) => {
		if (isLoginOrEmailChanged()) {
			setLoginChanged(false);
			setPasswordChanged(false);
			setLoginMessage('');
			setLoading(true);

			const token = await executeRecaptcha('LOGIN');

			await props
				.loginUser(
					{
						login: data.email,
						password: data.password,
						keepSignedIn: data.keepSignedIn,
						recaptchaToken: token,
					},
					coupon,
				)
				.then((resp: LoginResponse) => {
					if (resp && !resp.isError) {
						setIsInitialScanCompleted(true);
						if (data.enableBiometry) {
							triggerNewBiometricSession(data);
							triggerSetIsBiometryEnabled(true);
							localStorage.setItem('enableBiometry', 'true');
						} else {
							localStorage.setItem('enableBiometry', 'false');
							triggerSetIsBiometryEnabled(false);
						}
						if (isFirstLogin) {
							setDataLayer(LoginEvents.DATALAYER_FIRSTLOGIN_SUCCESS);
							sessionStorage.removeItem('isNewAccount');
						}
						handleRedirection();
					} else {
						setLoading(false);
						if (resp && resp.isError) {
							if (resp.code === ErrorCodes.customerAccountNotActivated) {
								setDataLayer(RegisterEvents.DATALAYER_REGISTER_ACCOUNT_INACTIVE('manual'));
							}
							setCurrentError(resp.code);
							setLoginMessage(
								`${t(LoginErrors.get(resp.code) || 'genericErrorMessage')} ${
									resp.code === ErrorCodes.selfExclusion ? resp.message : ''
								}`,
							);
						} else {
							setCurrentError(-1);
							setLoginMessage(resp ? resp.message : t('genericErrorMessage'));
						}
						triggerSetLoginFailed();
					}
				});
		}
	};

	return (
		<>
			<HelmetMetaTags title="Logowanie | BETFAN" description="Zakłady bukmacherskie online – legalny bukmacher BETFAN" ogurl={APP_URL.login} />
			<div className={cn('loginForm', props.className)}>
				<div className="loginForm__title">
					<p className="loginForm__title__text">Zaloguj się na konto</p>
					{!!props.onIconClick && <CloseForm onClick={props.onIconClick} />}
				</div>
				<Formik
					enableReinitialize={true}
					initialValues={{
						...props.initialValues,
						enableBiometry: isBiometryEnabled,
					}}
					validationSchema={loginFormValidationSchema()}
					validateOnBlur={true}
					validateOnChange={true}
					onSubmit={onLoginSubmit}
				>
					{({ handleSubmit, handleBlur, setFieldValue, values, errors, touched }) => {
						subscribeToBiometricScan((isSuccess, loginData) => {
							if (isSuccess) {
								setFieldValue('email', loginData?.email);
								setFieldValue('password', loginData?.password);
								handleSubmit();
							}
						});

						return (
							<form className="loginForm__form" onSubmit={handleSubmit}>
								<FormRow>
									<InputText
										className="login-form-input"
										name="email"
										label={t('email')}
										type="email"
										values={values}
										onBlur={handleBlur}
										errors={errors}
										isFieldValueValid={isFieldValueValid}
										touched={touched}
										setFieldValue={setFieldValue}
										onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
											setLoginChanged(true);
											const email = e.target.value.trim();
											setFieldValue('email', email);
										}}
										cyTestRef="email-input"
										backgroundColor="grey"
									/>
								</FormRow>
								<FormRow>
									<InputText
										className="login-form-input"
										name="password"
										label={t('password')}
										type={inputType}
										values={values}
										onBlur={handleBlur}
										errors={errors}
										isFieldValueValid={isFieldValueValid}
										touched={touched}
										setFieldValue={setFieldValue}
										onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
											setPasswordChanged(true);
											const password = e.target.value;
											setFieldValue('password', password);
										}}
										cyTestRef="password-input"
										backgroundColor="grey"
									/>
									<HidePassword
										onClick={() => {
											if (values.password) setInputType(inputType === 'password' ? 'text' : 'password');
										}}
										inputType={inputType}
										error={Boolean(errors['password'])}
									/>
								</FormRow>
								<div className="loginForm__checkboxes">
									<FormRow>
										<Checkbox
											name="keepSignedIn"
											label={t('rememberMe')}
											values={values}
											isFieldValueValid={isFieldValueValid}
											touched={touched}
											errors={errors}
											setFieldValue={setFieldValue}
											cyTestRef="checkbox-input"
										/>
									</FormRow>
									{isBiometryAvailable && (
										<FormRow>
											<Checkbox
												name="enableBiometry"
												label={t('nextTimeUseBiometricLogin')}
												values={values}
												isFieldValueValid={isFieldValueValid}
												touched={touched}
												errors={errors}
												setFieldValue={setFieldValue}
											/>
										</FormRow>
									)}
								</div>

								<div className="loginForm__cta">
									<Button cyTestRef="login-button" type="submit" disabled={loading} btnHeight="large">
										{loading ? <CircularProgress size="20px" color="primary" /> : 'Zaloguj się'}
									</Button>
									{isMobile() || (!isMobile() && window.location.pathname === APP_URL.login) ? (
										<div data-cy="forgot-button" className="loginForm__cta__password" onClick={() => props.handleNavTab()}>
											{t('forgotPassword')}
										</div>
									) : (
										<div data-cy="forgot-button" className="loginForm__cta__password" onClick={handleOpenReminder}>
											{t('forgotPassword')}
										</div>
									)}
								</div>
								{(loginMessage || showBiometricError) && (
									<p className="loginForm__form__error">{loginMessage || showBiometricError}</p>
								)}
							</form>
						);
					}}
				</Formik>
				<div className="loginForm__register">
					<div className="loginForm__register__text">{t('registrationText1')}</div>
					<Button type="button" cyTestRef="reg-button" btnType="secondary" onClick={handleRegister} btnHeight="large">
						{t('signUp')}
					</Button>
				</div>
			</div>
		</>
	);
};

export default LoginForm;
